import { Injectable } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { AngularPlugin } from "@microsoft/applicationinsights-angularplugin-js";
import { filter } from "rxjs/operators";
import { environment } from "../../environments/environment";

@Injectable({ providedIn: "root" })
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private angularPlugin = new AngularPlugin();
  private routerRef: Router | null = null;

  constructor(private router: Router) {
    this.routerRef = router;
  }

  /**
   * Initialize Application Insights. Safe to call multiple times.
   */
  init(): void {
    if (this.appInsights) return;
    const conn = environment.appInsights?.connectionString || "";
    if (!conn) {
      console.warn(
        "Application Insights connection string is not set. Telemetry is disabled."
      );
      return;
    }

    console.log("Initializing Application Insights");
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: conn,
        samplingPercentage: 25,
        extensions: [this.angularPlugin],
        extensionConfig: {
          [this.angularPlugin.identifier]: {
            router: this.routerRef
          }
        }
      }
    });

    this.appInsights.loadAppInsights();
    this.registerRouteTracking();
  }

  private registerRouteTracking() {
    if (!this.appInsights) return;
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((nav: any) => {
        try {
          this.appInsights!.trackPageView({
            name: nav?.urlAfterRedirects || nav?.url
          });
        } catch (e) {
          // swallow
        }
      });
  }

  trackEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights?.trackEvent({ name }, properties);
  }

  trackException(exception: any) {
    this.appInsights?.trackException({ exception });
  }

  isEnabled(): boolean {
    return !!this.appInsights;
  }
}
